<template>
  <div class="flow-map-wrapper">
    <div ref="container" class="flow-map-main">
      <svg ref="svg"></svg>
    </div>

    <!-- Side panel -->
    <transition name="slide-panel">
      <div v-if="selectedNode" class="flow-panel">
        <div class="flow-panel-header">
          <div>
            <h3>{{ selectedNode.label }}</h3>
            <div
              v-if="selectedNode.medianIncome"
              class="flow-panel-income-badge"
              :class="incomeBracket(selectedNode).class"
            >
              {{ incomeBracket(selectedNode).label }}
            </div>
          </div>
          <button class="flow-panel-close" @click="clearSelection">
            &times;
          </button>
        </div>

        <div class="flow-panel-stats">
          <div v-if="selectedNode.medianIncome" class="flow-stat">
            <span class="flow-stat-value">
              {{ formatIncome(selectedNode.medianIncome) }}
            </span>
            <span class="flow-stat-label">Median inkomst</span>
          </div>
          <div class="flow-stat">
            <span
              class="flow-stat-value"
              :class="{
                positive: netFlow(selectedNode) > 0,
                negative: netFlow(selectedNode) < 0
              }"
            >
              {{ netFlow(selectedNode) > 0 ? '+' : ''
              }}{{ netFlow(selectedNode) }}
            </span>
            <span class="flow-stat-label">Nettoflytt</span>
          </div>
          <div class="flow-stat">
            <span class="flow-stat-value">{{ selectedNode.incoming }}</span>
            <span class="flow-stat-label">Inflöde</span>
          </div>
          <div class="flow-stat">
            <span class="flow-stat-value">{{ selectedNode.outgoing }}</span>
            <span class="flow-stat-label">Utflöde</span>
          </div>
          <div v-if="selectedNode.internal" class="flow-stat">
            <span class="flow-stat-value internal">
              {{ selectedNode.internal }}
            </span>
            <span class="flow-stat-label">Internt</span>
          </div>
        </div>

        <div class="flow-panel-columns">
          <div
            v-if="incomingDetails(selectedNode).length"
            class="flow-panel-section"
          >
            <h4 class="section-title incoming">
              &#8595; Inflöde ({{ selectedNode.incoming }} ärenden)
            </h4>
            <div
              v-for="detail in incomingDetails(selectedNode)"
              :key="'in-' + detail.label"
              class="flow-detail-row"
            >
              <span class="flow-detail-label">{{ detail.label }}</span>
              <div class="flow-detail-bar-wrap">
                <div
                  class="flow-detail-bar"
                  :style="{
                    width: barWidth(detail.count) + 'px',
                    backgroundColor: detail.color
                  }"
                ></div>
              </div>
              <span class="flow-detail-count">{{ detail.count }}</span>
            </div>
          </div>

          <div
            v-if="outgoingDetails(selectedNode).length"
            class="flow-panel-section"
          >
            <h4 class="section-title outgoing">
              &#8593; Utflöde ({{ selectedNode.outgoing }} ärenden)
            </h4>
            <div
              v-for="detail in outgoingDetails(selectedNode)"
              :key="'out-' + detail.label"
              class="flow-detail-row"
            >
              <span class="flow-detail-label">{{ detail.label }}</span>
              <div class="flow-detail-bar-wrap">
                <div
                  class="flow-detail-bar"
                  :style="{
                    width: barWidth(detail.count) + 'px',
                    backgroundColor: detail.color
                  }"
                ></div>
              </div>
              <span class="flow-detail-count">{{ detail.count }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as d3 from 'd3';

const PALETTE = {
  bg: '#f8f8f8',
  paper: '#f8f8f8',
  ink: '#2c3e50',
  dim: '#878b9d',
  faint: '#e6e6e6',
  border: '#e6e6e6',
  teal: '#409fff',
  rose: '#B84040',
  slate: '#3A5A72',
  gold: '#2d7abf',
  green: '#3A6B44',
  purple: '#6B4A8A',
  amber: '#C8822A'
};

export default {
  props: {
    nodes: { type: Array, required: true },
    rawFlows: { type: Array, required: true },
    links: { type: Array, required: true },
    maxFlowCount: { type: Number, required: true },
    colorMap: { type: Object, required: true }
  },
  data() {
    return {
      selectedNode: null,
      simulation: null,
      resizeObserver: null,
      d3Initialized: false
    };
  },
  watch: {
    nodes: {
      handler() {
        if (this.nodes.length && !this.d3Initialized) {
          this.$nextTick(() => this.initD3());
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    if (this.simulation) this.simulation.stop();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    initD3() {
      const container = this.$refs.container;
      const svgEl = this.$refs.svg;
      if (!container || !svgEl || this.d3Initialized) return;
      this.d3Initialized = true;

      const rect = container.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      const svg = d3.select(svgEl).attr('width', width).attr('height', height);

      const defs = svg.append('defs');
      defs
        .append('pattern')
        .attr('id', 'dot-pattern')
        .attr('width', 28)
        .attr('height', 28)
        .attr('patternUnits', 'userSpaceOnUse')
        .append('circle')
        .attr('cx', 14)
        .attr('cy', 14)
        .attr('r', 0.8)
        .attr('fill', PALETTE.border);

      this.links.forEach((link, i) => {
        const targetId =
          typeof link.target === 'object' ? link.target.id : link.target;
        defs
          .append('marker')
          .attr('id', `arrow-${i}`)
          .attr('viewBox', '0 -4 10 8')
          .attr('refX', 0)
          .attr('refY', 0)
          .attr('markerWidth', 10)
          .attr('markerHeight', 8)
          .attr('markerUnits', 'userSpaceOnUse')
          .attr('orient', 'auto')
          .append('path')
          .attr('d', 'M0,-4L10,0L0,4Z')
          .attr('fill', this.colorMap[targetId] || PALETTE.slate);
      });

      svg
        .append('rect')
        .attr('width', width)
        .attr('height', height)
        .attr('fill', 'url(#dot-pattern)')
        .attr('opacity', 0.5);

      const g = svg.append('g').attr('class', 'zoom-group');

      const zoom = d3
        .zoom()
        .scaleExtent([0.3, 5])
        .on('zoom', event => {
          g.attr('transform', event.transform);
        });

      svg.call(zoom);

      svg.on('click', event => {
        if (event.target === svgEl || event.target.tagName === 'rect') {
          this.clearSelection();
        }
      });

      const padding = 80;
      const lats = this.nodes.map(n => n.lat);
      const lngs = this.nodes.map(n => n.lng);
      const xScale = d3
        .scaleLinear()
        .domain([Math.min(...lngs), Math.max(...lngs)])
        .range([padding, width - padding]);
      const yScale = d3
        .scaleLinear()
        .domain([Math.min(...lats), Math.max(...lats)])
        .range([height - padding, padding]);

      this.nodes.forEach(n => {
        n.x = xScale(n.lng);
        n.y = yScale(n.lat);
        n.fx = null;
        n.fy = null;
        const total = n.outgoing + n.incoming + (n.internal || 0);
        n.radius = Math.min(8 + total * 1.5, 28);
      });

      this._svg = svg;
      this._g = g;
      this._zoom = zoom;
      this._width = width;
      this._height = height;

      this._linkGroup = g.append('g').attr('class', 'links');
      this._nodeGroup = g.append('g').attr('class', 'nodes');

      this.drawLinks();
      this.drawNodes();

      this.simulation = d3
        .forceSimulation(this.nodes)
        .force(
          'link',
          d3
            .forceLink(this.links)
            .id(d => d.id)
            .distance(120)
            .strength(0.3)
        )
        .force('charge', d3.forceManyBody().strength(-200))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force(
          'collision',
          d3.forceCollide().radius(d => d.radius + 10)
        )
        .force(
          'x',
          d3
            .forceX()
            .x(d => xScale(d.lng))
            .strength(0.3)
        )
        .force(
          'y',
          d3
            .forceY()
            .y(d => yScale(d.lat))
            .strength(0.3)
        )
        .alpha(0.8)
        .alphaDecay(0.05)
        .on('tick', () => {
          this.ticked();
          if (!this._hasFittedBounds) {
            this._tickCount = (this._tickCount || 0) + 1;
            if (this._tickCount === 15) {
              this._hasFittedBounds = true;
              this.fitBounds();
            }
          }
        });

      this.resizeObserver = new ResizeObserver(entries => {
        const { width: w, height: h } = entries[0].contentRect;
        svg.attr('width', w).attr('height', h);
        this._width = w;
        this._height = h;
      });
      this.resizeObserver.observe(container);
    },

    drawLinks() {
      this._linkGroup
        .selectAll('.flow-link')
        .data(this.links, (d, i) => i)
        .enter()
        .append('path')
        .attr('class', 'flow-link')
        .attr('fill', 'none')
        .attr('stroke-linecap', 'round')
        .attr('marker-end', (d, i) => `url(#arrow-${i})`)
        .attr('stroke', d => {
          const targetId =
            typeof d.target === 'object' ? d.target.id : d.target;
          return this.colorMap[targetId] || PALETTE.slate;
        })
        .attr('stroke-width', d =>
          Math.max(0.5, (d.count / this.maxFlowCount) * 2)
        )
        .attr('opacity', 0)
        .transition()
        .duration(800)
        .attr('opacity', d => Math.min(0.7, d.count / this.maxFlowCount + 0.2));

      this._linkElements = this._linkGroup.selectAll('.flow-link');
    },

    drawNodes() {
      const that = this;

      const drag = d3
        .drag()
        .on('start', (event, d) => {
          if (!event.active) this.simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) this.simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        });

      const nodeG = this._nodeGroup
        .selectAll('.node-group')
        .data(this.nodes, d => d.id)
        .enter()
        .append('g')
        .attr('class', 'node-group')
        .attr('cursor', 'pointer')
        .call(drag)
        .on('click', (event, d) => {
          event.stopPropagation();
          this.selectNode(d);
        })
        .on('mouseenter', function (event, d) {
          d3.select(this)
            .select('.node-glow')
            .transition()
            .duration(200)
            .attr('r', d.radius * 2.5)
            .attr('opacity', 0.25);
          d3.select(this)
            .select('.node-circle')
            .transition()
            .duration(200)
            .attr('r', d.radius * 1.15);
        })
        .on('mouseleave', function (event, d) {
          d3.select(this)
            .select('.node-glow')
            .transition()
            .duration(200)
            .attr('r', 0)
            .attr('opacity', 0);
          d3.select(this)
            .select('.node-circle')
            .transition()
            .duration(200)
            .attr('r', d.radius);
        });

      nodeG
        .append('circle')
        .attr('class', 'node-glow')
        .attr('r', 0)
        .attr('fill', d => that.getNodeColor(d))
        .attr('opacity', 0);

      nodeG
        .append('circle')
        .attr('class', 'node-circle')
        .attr('r', 0)
        .attr('fill', d => that.getNodeColor(d))
        .attr('stroke', PALETTE.paper)
        .attr('stroke-width', 1.5)
        .transition()
        .duration(600)
        .delay((d, i) => i * 60)
        .attr('r', d => d.radius);

      const badgeG = nodeG
        .filter(d => d.internal > 0)
        .append('g')
        .attr('class', 'internal-badge')
        .attr('transform', d => `translate(0, ${-d.radius - 18})`);

      badgeG
        .append('rect')
        .attr('rx', 8)
        .attr('ry', 8)
        .attr('fill', '#2d7abf')
        .attr('opacity', 0.9)
        .attr('x', -14)
        .attr('y', -10)
        .attr('width', 28)
        .attr('height', 18);

      badgeG
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', 2)
        .attr('fill', '#fff')
        .attr('font-size', '10px')
        .attr('font-weight', 700)
        .attr('font-family', "'Averta Std', Helvetica, sans-serif")
        .text(d => d.internal);

      nodeG
        .append('text')
        .attr('class', 'node-label')
        .attr('text-anchor', 'middle')
        .attr('dy', d => d.radius + 15)
        .attr('fill', PALETTE.ink)
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('font-family', "'Averta Std', Helvetica, sans-serif")
        .attr('opacity', 0)
        .text(d => d.label)
        .transition()
        .duration(600)
        .delay((d, i) => i * 60 + 300)
        .attr('opacity', 1);

      this._nodeElements = this._nodeGroup.selectAll('.node-group');
    },

    ticked() {
      this._linkElements.attr('d', d => {
        const sx = d.source.x;
        const sy = d.source.y;
        const tx = d.target.x;
        const ty = d.target.y;
        const dx = tx - sx;
        const dy = ty - sy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = -dy / dist;
        const ny = dx / dist;
        const curvature = Math.min(dist * 0.25, 60);
        const cx = (sx + tx) / 2 + nx * curvature;
        const cy = (sy + ty) / 2 + ny * curvature;
        const startR = d.source.radius + 2;
        const endR = d.target.radius + 14;
        const angleS = Math.atan2(cy - sy, cx - sx);
        const angleE = Math.atan2(ty - cy, tx - cx);
        const x1 = sx + Math.cos(angleS) * startR;
        const y1 = sy + Math.sin(angleS) * startR;
        const x2 = tx - Math.cos(angleE) * endR;
        const y2 = ty - Math.sin(angleE) * endR;
        return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
      });
      this._nodeElements.attr('transform', d => `translate(${d.x},${d.y})`);
    },

    fitBounds() {
      const pad = 60;
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      this.nodes.forEach(n => {
        minX = Math.min(minX, n.x - n.radius);
        minY = Math.min(minY, n.y - n.radius - 20);
        maxX = Math.max(maxX, n.x + n.radius);
        maxY = Math.max(maxY, n.y + n.radius + 20);
      });
      const bw = maxX - minX + pad * 2;
      const bh = maxY - minY + pad * 2;
      const scale = Math.min(this._width / bw, this._height / bh, 2);
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      this._svg
        .transition()
        .duration(600)
        .call(
          this._zoom.transform,
          d3.zoomIdentity
            .translate(this._width / 2, this._height / 2)
            .scale(scale)
            .translate(-cx, -cy)
        );
    },

    selectNode(d) {
      this.selectedNode = this.selectedNode?.id === d.id ? null : d;
      this.updateHighlight();
    },

    clearSelection() {
      this.selectedNode = null;
      this.updateHighlight();
    },

    updateHighlight() {
      const sel = this.selectedNode;
      const duration = 400;

      this._linkElements
        .transition()
        .duration(duration)
        .attr('opacity', d => {
          if (!sel) return Math.min(0.7, d.count / this.maxFlowCount + 0.2);
          const sid = typeof d.source === 'object' ? d.source.id : d.source;
          const tid = typeof d.target === 'object' ? d.target.id : d.target;
          if (sid === sel.id)
            return Math.min(0.85, d.count / this.maxFlowCount + 0.3);
          if (tid === sel.id) return 0.15;
          return 0.04;
        })
        .attr('stroke-width', d => {
          if (!sel) return Math.max(0.5, (d.count / this.maxFlowCount) * 2);
          const sid = typeof d.source === 'object' ? d.source.id : d.source;
          const tid = typeof d.target === 'object' ? d.target.id : d.target;
          if (sid === sel.id)
            return Math.max(1, (d.count / this.maxFlowCount) * 3);
          if (tid === sel.id)
            return Math.max(0.5, (d.count / this.maxFlowCount) * 2);
          return 0.3;
        });

      this._nodeElements.each(function (d) {
        const isSelected = sel?.id === d.id;
        if (sel && !sel._connectedIds) {
          sel._connectedIds = new Set();
          d3.selectAll('.flow-link').each(function (l) {
            const sid = typeof l.source === 'object' ? l.source.id : l.source;
            const tid = typeof l.target === 'object' ? l.target.id : l.target;
            if (sid === sel.id) sel._connectedIds.add(tid);
            if (tid === sel.id) sel._connectedIds.add(sid);
          });
        }
        const connected = !sel || isSelected || sel._connectedIds?.has(d.id);

        d3.select(this)
          .select('.node-circle')
          .transition()
          .duration(duration)
          .attr('opacity', connected ? 1 : 0.15)
          .attr('r', isSelected ? d.radius * 1.3 : d.radius)
          .attr('stroke-width', isSelected ? 2.5 : 1.5);

        d3.select(this)
          .select('.node-label')
          .transition()
          .duration(duration)
          .attr('opacity', connected ? 1 : 0.1)
          .attr('font-weight', isSelected ? 800 : 600)
          .attr('font-size', isSelected ? '13px' : '11px');

        if (isSelected) {
          d3.select(this)
            .select('.node-glow')
            .transition()
            .duration(duration)
            .attr('r', d.radius * 2.5)
            .attr('opacity', 0.2);
        } else {
          d3.select(this)
            .select('.node-glow')
            .transition()
            .duration(duration)
            .attr('r', 0)
            .attr('opacity', 0);
        }
      });
    },

    formatIncome(income) {
      if (!income) return '';
      if (income >= 1000) return Math.round(income / 1000) + 'k kr';
      return income + ' kr';
    },

    incomeBracket(node) {
      const income = node.medianIncome;
      if (!income) return { label: '', class: '' };
      if (income >= 55000) {
        return { label: 'Höginkomst', class: 'income-high' };
      }
      if (income >= 42000) {
        return { label: 'Medelinkomst', class: 'income-mid' };
      }
      if (income >= 30000) {
        return { label: 'Låg-medelinkomst', class: 'income-low-mid' };
      }
      return { label: 'Låginkomst', class: 'income-low' };
    },

    getNodeColor() {
      return '#409fff';
    },

    netFlow(node) {
      return node.incoming - node.outgoing;
    },

    barWidth(count) {
      return Math.max(8, (count / this.maxFlowCount) * 50);
    },

    outgoingDetails(node) {
      return this.rawFlows
        .filter(f => f.from === node.id)
        .map(f => ({
          label: f.to,
          count: f.count,
          color: this.colorMap[f.to] || PALETTE.slate
        }))
        .sort((a, b) => b.count - a.count);
    },

    incomingDetails(node) {
      return this.rawFlows
        .filter(f => f.to === node.id)
        .map(f => ({
          label: f.from,
          count: f.count,
          color: this.colorMap[f.to] || PALETTE.slate
        }))
        .sort((a, b) => b.count - a.count);
    }
  }
};
</script>

<style scoped>
.flow-map-wrapper {
  display: flex;
  position: relative;
  min-height: 550px;
  background: #f8f7f7;
}

.flow-map-main {
  flex: 1;
  min-width: 0;
  position: relative;
  min-height: 550px;
}

.flow-map-main svg {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 550px;
}

.flow-map-legend {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: #f8f8f8ee;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 1rem;
  pointer-events: none;
}

.legend-title {
  font-weight: 700;
  color: #878b9d;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
  font-size: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #878b9d;
  margin-bottom: 3px;
  font-size: 1.1rem;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.flow-panel {
  width: 300px;
  background: #f8f8f8;
  border-left: 1px solid #e6e6e6;
  padding: 18px 16px;
  overflow-y: auto;
  max-height: 550px;
  display: flex;
  flex-direction: column;
}

.flow-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.4rem;
}

.flow-panel-header h3 {
  font-size: 1.8rem;
  font-weight: 800;
  color: #2c3e50;
  margin: 0;
  line-height: 1.2;
}

.flow-panel-income-badge {
  display: inline-block;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 10px;
  border-radius: 4px;
  margin-top: 6px;
}

.flow-panel-income-badge.income-high {
  background: #dbeafe;
  color: #1e40af;
}

.flow-panel-income-badge.income-mid {
  background: #dbeafe;
  color: #1e40af;
}

.flow-panel-income-badge.income-low-mid {
  background: #e0e7ff;
  color: #3730a3;
}

.flow-panel-income-badge.income-low {
  background: #fee2e2;
  color: #991b1b;
}

.flow-panel-close {
  background: none;
  border: none;
  font-size: 2.2rem;
  color: #878b9d;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.flow-panel-close:hover {
  color: #2c3e50;
}

.flow-panel-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 8px;
  margin-bottom: 1.6rem;
}

.flow-stat {
  text-align: center;
  background: #eef2f7;
  border-radius: 8px;
  padding: 10px 6px;
}

.flow-stat-value {
  display: block;
  font-size: 1.8rem;
  font-weight: 800;
  color: #2c3e50;
}

.flow-stat-value.positive {
  color: #3a6b44;
}

.flow-stat-value.negative {
  color: #b84040;
}

.flow-stat-value.internal {
  color: #409fff;
}

.flow-stat-label {
  font-size: 0.9rem;
  color: #878b9d;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.flow-panel-columns {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.flow-panel-section {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px 0;
}

.section-title.incoming {
  color: #3a6b44;
}

.section-title.outgoing {
  color: #b84040;
}

.flow-detail-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #e6e6e6;
  gap: 6px;
}

.flow-detail-label {
  font-size: 1.1rem;
  color: #2c3e50;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70px;
}

.flow-detail-bar-wrap {
  flex: 1;
  display: flex;
  align-items: center;
}

.flow-detail-bar {
  height: 4px;
  border-radius: 2px;
}

.flow-detail-count {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  min-width: 20px;
  text-align: right;
}
</style>
